import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BookOpen, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AutoLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setErrorMessage('Link inválido. No se encontró el token de acceso.');
      return;
    }

    // Call magic login endpoint
    const performAutoLogin = async () => {
      try {
        const response = await axios.get(`${API}/reader/magic-login?token=${token}`);
        
        // Save session
        localStorage.setItem('reader_token', response.data.token);
        localStorage.setItem('reader_user', JSON.stringify(response.data.user));
        
        setStatus('success');
        toast.success(`¡Bienvenido, ${response.data.user.name}!`);
        
        // Redirect to biblioteca after short delay
        setTimeout(() => {
          navigate('/reader/biblioteca');
        }, 1500);
        
      } catch (error) {
        setStatus('error');
        const message = error.response?.data?.detail || 'Error al iniciar sesión';
        setErrorMessage(message);
        toast.error(message);
      }
    };

    performAutoLogin();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center">
        {/* Logo */}
        <div className="mb-8">
          <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <BookOpen className="text-white" size={40} />
          </div>
          <h1 className="text-2xl font-serif font-bold text-white">Raíz Lecturas</h1>
        </div>

        {/* Status Messages */}
        {status === 'loading' && (
          <div className="bg-gray-800/50 rounded-2xl p-8 border border-gray-700">
            <Loader2 className="animate-spin text-amber-500 mx-auto mb-4" size={48} />
            <h2 className="text-xl font-semibold text-white mb-2">Iniciando sesión...</h2>
            <p className="text-gray-400">Preparando tu biblioteca personal</p>
          </div>
        )}

        {status === 'success' && (
          <div className="bg-green-900/30 rounded-2xl p-8 border border-green-700">
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">✓</span>
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">¡Sesión iniciada!</h2>
            <p className="text-gray-300">Redirigiendo a tu biblioteca...</p>
          </div>
        )}

        {status === 'error' && (
          <div className="bg-red-900/30 rounded-2xl p-8 border border-red-700">
            <AlertCircle className="text-red-500 mx-auto mb-4" size={48} />
            <h2 className="text-xl font-semibold text-white mb-2">Error de acceso</h2>
            <p className="text-gray-300 mb-6">{errorMessage}</p>
            <button
              onClick={() => navigate('/reader/login')}
              className="px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-full font-semibold transition-colors"
            >
              Iniciar sesión manualmente
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AutoLogin;
